import { useCallback, useEffect, useState } from 'react';
import type { StatusResponse } from '@status/shared';
import { DEFAULT_PERIOD, fetchStatus, PERIODS, type Period } from './lib/api';
import { ProviderCard } from './components/ProviderCard';
import { Summary } from './components/Summary';

const THEME_KEY = 'status-manager-theme';
const REFRESH_MS = 60_000;
const SITE_TITLE = (import.meta.env['VITE_SITE_TITLE'] as string | undefined) ?? '大模型状态监控';

function useTheme(): [boolean, () => void] {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggle = useCallback(() => {
    setDark((prev) => {
      const next = !prev;
      document.documentElement.classList.toggle('dark', next);
      document.documentElement.style.colorScheme = next ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_KEY, next ? 'dark' : 'light');
      } catch {
        // 隐私模式下写不进去，仅本次会话生效
      }
      return next;
    });
  }, []);

  return [dark, toggle];
}

export function App(): React.JSX.Element {
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD);
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dark, toggleTheme] = useTheme();

  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const next = await fetchStatus(period, controller.signal);
        if (cancelled) return;
        setData(next);
        setError(null);
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        if (!cancelled) {
          setLoading(false);
          // 定时器链而非 setInterval：请求慢时不会堆积
          timer = window.setTimeout(() => void load(), REFRESH_MS);
        }
      }
    };

    setLoading(true);
    void load();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [period]);

  // 配置允许每个渠道单独设 probe_url，所以去重后全部列出，
  // 不能只取 groups[0] —— 将来监控多个网关时那样会漏掉其余的。
  const probeTargets =
    data === null
      ? '上游网关'
      : [...new Set(data.groups.map((g) => g.probe_url).filter((u) => u !== ''))].join('、') ||
        '上游网关';

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      {/* 不用 flex-wrap：控件整块换到下一行会让标题区突然变高。
          改为让标题块可收缩（min-w-0）、控件块不收缩，宽度不够时由副标题截断。 */}
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-bold tracking-tight">{SITE_TITLE}</h1>
          {/* 探测配置并进副标题，不再单独占一行页脚。data 为 null 时只显示说明文字，
              避免先渲染一串「—」再跳成真实值。 */}
          <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
            对 {probeTargets} 按模型逐一发起真实调用，记录可用性与延迟
            {data !== null && (
              <>
                ，每 {Math.round(data.meta.interval_ms / 60_000)} 分钟一轮，超过{' '}
                {data.meta.slow_latency_ms / 1000}s 记为降级
              </>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* 平铺分段选择器。用 radiogroup 而不是一排 button：
              键盘和读屏软件能识别出「四个选项里选中一个」，button 做不到。 */}
          <div
            role="radiogroup"
            aria-label="统计周期"
            className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900"
          >
            {PERIODS.map((p) => {
              const active = p.value === period;
              return (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setPeriod(p.value)}
                  className={`rounded-md px-2.5 py-1 text-sm transition-colors ${
                    active
                      ? 'bg-slate-900 font-medium text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={dark ? '切换到浅色主题' : '切换到深色主题'}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
          >
            {dark ? '浅色' : '深色'}
          </button>
        </div>
      </header>

      {error !== null && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
        >
          加载失败：{error}
          {data !== null && <>（下方显示的是上一次成功获取的数据）</>}
        </div>
      )}

      {loading && data === null && (
        <p className="text-sm text-slate-500 dark:text-slate-400">加载中…</p>
      )}

      {data !== null && (
        <div className="space-y-5">
          <Summary data={data} />
          <div className="space-y-4">
            {data.groups.map((group) => (
              <ProviderCard key={group.provider_slug} group={group} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
